import React, { useContext, useState, useEffect } from 'react';
import { ActivityContext } from 'state/providers/ActivityProvider';
import { InstructionButtonContext } from 'state/providers/InstructionsButtonProvider';
import useActivityAudio from '@monorepo/shared/src/hooks/use-activity-audio';
import { GuessStates } from 'types';
import FeelingsMatchRoundLayout from './FMatchRoundLayout';
import FMatchFinalRound from './FMatchFinalRound';
import { formatCards } from '../utils/format-util';
import ActivityPlayButtonOverlay from '../../components/ActivityPlayButtonOverlay';

const FeelingsMatchRound: React.FC = () => {
  const [{ sounds, isPlaying }, { playSoundFromState }] = useActivityAudio();
  const [state] = useContext(ActivityContext);
  const [{ playInstructionFlag, currentInstruction }, { stopInstructions, setCurrentInstruction }] =
    useContext(InstructionButtonContext);
  const { instructions, cards } = state.currentRound?.data ?? {};
  const [cardsRowOne, setCardsRowOne] = useState<Array<any>>([]);
  const [cardsRowTwo, setCardsRowTwo] = useState<Array<any>>([]);
  const [isCardInAnimation, setIsCardInAnimation] = useState<boolean>(false);
  const [guessIndicator, setGuessIndicator] = useState(GuessStates.NULL);
  const [roundIndex, setRoundIndex] = useState(0);
  const [showActivityPlayButton, setShowActivityPlayButton] = useState<boolean>(true);

  const { rounds } = state.activity;
  const isFinalRound = roundIndex === rounds.length - 1;

  useEffect(() => {
    const cardsArr = cards || [];
    if (!cardsRowOne.length)
      setCardsRowOne(formatCards(cardsArr, 1).sort(() => Math.random() - 0.5));

    if (!cardsRowTwo.length)
      setCardsRowTwo(formatCards(cardsArr, 2).sort(() => Math.random() - 0.5));
  }, [rounds, roundIndex]);

  useEffect(() => {
    const playSound = async (currentInstruction) => {
      await playSoundFromState(sounds[currentInstruction], true);
      stopInstructions();
    };
    if (currentInstruction && playInstructionFlag) {
      playSound(currentInstruction);
    }
  }, [playInstructionFlag, currentInstruction]);

  useEffect(() => {
    if (instructions?.introduction?.description) {
      setCurrentInstruction(instructions.introduction.description);
    }
  }, [instructions.introduction.description]);

  const resetCards = () => {
    setCardsRowOne(formatCards(cardsRowOne, 1));
    setCardsRowTwo(formatCards(cardsRowTwo, 2));
  };

  const playCardSound = (feelingSound) => {
    setIsCardInAnimation(true);
    playSoundFromState(feelingSound, true);
    setTimeout(() => {
      setIsCardInAnimation(false);
    }, 500);
  };

  const isMatchGameOver = () => {
    const cardsDisplayed = [...cardsRowOne, ...cardsRowTwo].filter((c) => !c.removed);

    if (!cardsDisplayed.length) {
      playSoundFromState(sounds[instructions.end.description], true);

      setTimeout(() => {
        const index = roundIndex;
        setRoundIndex(index + 1);
      }, 3000);
    }
  };

  const handleIsAMatch = (card) => {
    setTimeout(async () => {
      setGuessIndicator(GuessStates.SUCCESS);
      playSoundFromState(sounds[card.affirmation.description], true);
    }, 1000);

    setTimeout(() => {
      removeCards();
      setGuessIndicator(GuessStates.NULL);
      isMatchGameOver();
    }, 2500);
  };

  const handleIsNotAMatch = () => {
    setTimeout(() => {
      setGuessIndicator(GuessStates.ERROR);
      playCardSound(sounds['boink']);
    }, 1000);

    setTimeout(() => {
      setGuessIndicator(GuessStates.NULL);
      resetCards();
    }, 2500);
  };

  const handleOnClick = (card, row) => {
    const cardsFlipped = [...cardsRowOne, ...cardsRowTwo].filter((c) => c.flipped);

    if (isCardInAnimation || guessIndicator !== GuessStates.NULL || cardsFlipped.length > 1) return;

    if (!card.flipped) {
      playCardSound(sounds[card.cardTouch.description]);
    }

    const cardId = Number(card.cardTouch.id);
    const cards = row === 1 ? cardsRowOne : cardsRowTwo;

    const updatedCards = cards.map((c) => {
      if (c.cardTouch.id === cardId) c.flipped = !c.flipped;
      return c;
    });

    if (row === 1) setCardsRowOne(updatedCards);
    if (row === 2) setCardsRowTwo(updatedCards);

    checkCardMatch(card, row);
  };

  const checkCardMatch = (card, row) => {
    const combinedFlipped = [...cardsRowOne, ...cardsRowTwo].filter((c) => c?.flipped)?.length;
    if (combinedFlipped !== 2) return;

    const otherCardRow = row === 1 ? cardsRowTwo : cardsRowOne;
    const foundOtherCard = otherCardRow.find(
      (c) => c?.cardTouch.id === card?.cardTouch.id && c.flipped
    );

    if (card?.flipped && foundOtherCard) {
      handleIsAMatch(card);
      return;
    }

    handleIsNotAMatch();
  };

  const removeCards = () => {
    const updatedCardsOne = cardsRowOne.map((c) => {
      if (c.flipped) {
        c.flipped = false;
        c.removed = true;
      }
      return c;
    });

    const updatedCardsTwo = cardsRowTwo.map((c) => {
      if (c.flipped) {
        c.removed = true;
        c.flipped = false;
      }
      return c;
    });

    setTimeout(() => {
      setCardsRowOne(updatedCardsOne);
      setCardsRowTwo(updatedCardsTwo);
    }, 2000);
  };

  const onActivityButtonClick = () => {
    setShowActivityPlayButton(false);

    if (!isPlaying && !isFinalRound)
      playSoundFromState(sounds[instructions.introduction.description]);
  };

  if (isFinalRound && rounds[roundIndex]) {
    return <FMatchFinalRound round={rounds[roundIndex]} />;
  }

  return (
    <>
      <FeelingsMatchRoundLayout
        guessIndicator={guessIndicator}
        cardsRowOne={cardsRowOne}
        cardsRowTwo={cardsRowTwo}
        handleOnClick={handleOnClick}
        cardFront={state.currentRound.data.cardFront}
      />
      {showActivityPlayButton && (
        <ActivityPlayButtonOverlay onOverlayClick={onActivityButtonClick} />
      )}
    </>
  );
};

export default FeelingsMatchRound;
